import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './CoursePage.css';

const CoursePage: React.FC = () => {
  const { courseSlug } = useParams<{ courseSlug: string }>();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    level: '',
    message: ''
  });

  const courseDetails = {
    'matura-podstawowa': {
      title: 'Matura Podstawowa',
      level: 'Klasa 3 liceum',
      description: 'Kompleksowe przygotowanie do matury z matematyki na poziomie podstawowym. Omówimy wszystkie niezbędne zagadnienia oraz rozwiążemy zadania z poprzednich lat.',
      features: [
        'Funkcje i ich właściwości',
        'Geometria płaska i przestrzenna',
        'Planimetria i stereometria',
        'Statystyka opisowa',
        'Prawdopodobieństwo',
        'Rozwiązywanie zadań maturalnych',
        'Techniki szybkiego liczenia',
        'Zarządzanie czasem na egzaminie'
      ],
      price: '160 zł/miesiąc',
      duration: '4 lekcje po 60 minut',
      icon: '📝'
    },
    'matura-rozszerzona': {
      title: 'Matura Rozszerzona',
      level: 'Klasa 3 liceum',
      description: 'Intensywne przygotowanie do matury rozszerzonej z matematyki. Dla uczniów chcących osiągnąć najwyższe wyniki i kontynuować naukę na studiach ścisłych.',
      features: [
        'Analiza matematyczna - pochodne i całki',
        'Geometria analityczna w przestrzeni',
        'Ciągi i szeregi liczbowe',
        'Kombinatoryka i prawdopodobieństwo',
        'Równania różniczkowe podstawowe',
        'Zadania optymalizacyjne',
        'Dowody matematyczne',
        'Strategie rozwiązywania zadań trudnych'
      ],
      price: '200 zł/miesiąc',
      duration: '6 lekcji po 60 minut',
      icon: '🎯'
    },
    'egzamin-osmoklasisty': {
      title: 'Egzamin Ósmoklasisty',
      level: 'Klasa 8',
      description: 'Przygotowanie do egzaminu ósmoklasisty z matematyki. Systematyczne powtórzenie materiału ze szkoły podstawowej i trening rozwiązywania zadań.',
      features: [
        'Działania na liczbach wymiernych',
        'Proporcjonalność i procenty',
        'Równania i nierówności',
        'Figury płaskie i przestrzenne',
        'Funkcje liniowe',
        'Statystyka i prawdopodobieństwo',
        'Zadania praktyczne i życiowe',
        'Techniki sprawdzania odpowiedzi'
      ],
      price: '140 zł/miesiąc',
      duration: '4 lekcje po 45 minut',
      icon: '📐'
    },
    'konkursy-olimpiady': {
      title: 'Konkursy i Olimpiady',
      level: 'Wszystkie poziomy',
      description: 'Specjalistyczne przygotowanie do konkursów i olimpiad matematycznych. Rozwijamy myślenie matematyczne i umiejętności rozwiązywania zadań niestandardowych.',
      features: [
        'Teoria liczb',
        'Kombinatoryka zaawansowana',
        'Geometria olimpijska',
        'Algebrapodstawowa i rozszerzona',
        'Nierówności matematyczne',
        'Metody rozwiązywania zadań nietypowych',
        'Analiza strategii konkursowych',
        'Przygotowanie psychologiczne do zawodów'
      ],
      price: '250 zł/miesiąc',
      duration: '6 lekcji po 90 minut',
      icon: '🏆'
    },
    'egzaminy-zagraniczne': {
      title: 'Egzaminy Zagraniczne',
      level: 'Liceum/Studia',
      description: 'Przygotowanie do międzynarodowych egzaminów matematycznych takich jak SAT, ACT, A-levels, IB czy egzaminów wstępnych na zagraniczne uczelnie.',
      features: [
        'SAT Math Level 1 i 2',
        'ACT Mathematics',
        'A-levels Mathematics',
        'IB Mathematics (HL/SL)',
        'Egzaminy wstępne na MIT, Harvard, Oxford',
        'Matematyka w języku angielskim',
        'Techniki rozwiązywania zadań wielokrotnego wyboru',
        'Zarządzanie czasem na egzaminie'
      ],
      price: '300 zł/miesiąc',
      duration: '6 lekcji po 60 minut',
      icon: '🌍'
    },
    'matematyka-podstawowa': {
      title: 'Szkoła Podstawowa',
      level: 'Klasy 1-6',
      description: 'Kompleksowy kurs matematyki dla uczniów szkół podstawowych. Budujemy solidne fundamenty matematyczne w przyjaznej atmosferze.',
      features: [
        'Działania na liczbach naturalnych i ułamkach',
        'Podstawy geometrii płaskiej',
        'Rozwiązywanie zadań tekstowych',
        'Gry i zabawy matematyczne',
        'Przygotowanie do sprawdzianów',
        'Indywidualne tempo nauki',
        'Metody wizualizacji matematyki',
        'Budowanie pewności siebie'
      ],
      price: '120 zł/miesiąc',
      duration: '4 lekcje po 45 minut',
      icon: '🔢'
    },
    'indywidualne-potrzeby': {
      title: 'Indywidualne Potrzeby',
      level: 'Dostosowany',
      description: 'Zajęcia szyte na miarę konkretnych potrzeb ucznia. Elastyczny program dostosowany do tempa nauki, poziomu i celów edukacyjnych.',
      features: [
        'Program dostosowany do ucznia',
        'Elastyczne terminy zajęć',
        'Koncentracja na problemowych zagadnieniach',
        'Indywidualne tempo nauki',
        'Wsparcie w konkretnych przedmiotach szkolnych',
        'Przygotowanie do konkretnych egzaminów',
        'Pomoc w bieżących zadaniach szkolnych',
        'Regularne konsultacje z rodzicami'
      ],
      price: '180-350 zł/miesiąc',
      duration: 'Ustalane indywidualnie',
      icon: '👤'
    },
    'nadrabianie-zaleglosci': {
      title: 'Nadrabianie Zaległości',
      level: 'Wszystkie poziomy',
      description: 'Intensywny kurs dla uczniów, którzy mają braki w wiedzy matematycznej. Szybkie i skuteczne uzupełnienie luk programowych.',
      features: [
        'Diagnoza poziomu wiedzy',
        'Plan nadrabiania zaległości',
        'Intensywne zajęcia uzupełniające',
        'Powtórzenie kluczowych zagadnień',
        'Ćwiczenia utrwalające',
        'Przygotowanie do sprawdzianów i klasówek',
        'Wsparcie psychologiczne',
        'Monitoring postępów nauki'
      ],
      price: '160 zł/miesiąc',
      duration: '6 lekcji po 60 minut',
      icon: '⚡'
    }
  };

  const courseInfo = courseDetails[courseSlug as keyof typeof courseDetails] || courseDetails['matematyka-podstawowa'];

  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": courseInfo.title,
    "description": courseInfo.description,
    "provider": {
      "@type": "EducationalOrganization",
      "name": "Sigma Study",
      "url": "https://sigmastudy.pl"
    },
    "offers": {
      "@type": "Offer",
      "price": courseInfo.price.replace(/[^\d]/g, ''),
      "priceCurrency": "PLN",
      "availability": "https://schema.org/InStock"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Strona główna",
        "item": "https://sigmastudy.pl/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Kursy",
        "item": "https://sigmastudy.pl/#courses"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": courseInfo.title,
        "item": `https://sigmastudy.pl/kurs/${courseSlug}`
      }
    ]
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert(`Dziękujemy za zainteresowanie kursem "${courseInfo.title}"! Skontaktujemy się z Tobą w ciągu 24 godzin.`);
    setFormData({
      name: '',
      email: '',
      phone: '',
      level: '',
      message: ''
    });
  };

  return (
    <div className="course-page">
      <Helmet>
        <title>{courseInfo.title} - Korepetycje z Matematyki | Sigma Study</title>
        <meta name="description" content={`${courseInfo.description} Cena: ${courseInfo.price}. Zapisz się na zajęcia w Sigma Study!`} />
        <link rel="canonical" href={`https://sigmastudy.pl/kurs/${courseSlug}`} />
        <meta property="og:title" content={`${courseInfo.title} - Korepetycje z Matematyki | Sigma Study`} />
        <meta property="og:description" content={courseInfo.description} />
        <meta property="og:url" content={`https://sigmastudy.pl/kurs/${courseSlug}`} />
        <meta name="twitter:title" content={`${courseInfo.title} - Korepetycje z Matematyki | Sigma Study`} />
        <meta name="twitter:description" content={courseInfo.description} />
        <script type="application/ld+json">
          {JSON.stringify(courseSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <section className="course-hero">
        <div className="course-hero-container">
          <div className="breadcrumb">
            <Link to="/">Strona główna</Link> → <Link to="/#courses">Kursy</Link> → <span>{courseInfo.title}</span>
          </div>

          <div className="course-hero-content">
            <div className="course-hero-icon">{courseInfo.icon}</div>
            <h1 className="course-hero-title">{courseInfo.title}</h1>
            <p className="course-hero-level">{courseInfo.level}</p>
            <p className="course-hero-description">{courseInfo.description}</p>

            <div className="course-quick-info">
              <div className="quick-info-item">
                <span className="info-icon">💰</span>
                <span>{courseInfo.price}</span>
              </div>
              <div className="quick-info-item">
                <span className="info-icon">⏰</span>
                <span>{courseInfo.duration}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="course-details">
        <div className="course-details-container">
          <div className="course-content-grid">
            <div className="course-features-section">
              <h2>Program kursu</h2>
              <ul className="course-features-list">
                {courseInfo.features.map((feature, index) => (
                  <li key={index}>
                    <span className="feature-checkmark">✅</span>
                    {feature}
                  </li>
                ))}
              </ul>

            </div>

            <div className="course-signup-section">
              <div className="signup-form-container">
                <h3>Zapisz się na lekcję!</h3>
                <p>Wypełnij formularz, a skontaktujemy się z Tobą w ciągu 24 godzin</p>

                <form className="course-signup-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name">Imię i nazwisko *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Wpisz swoje imię i nazwisko"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="twoj@email.pl"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Telefon</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+48 123 456 789"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="level">Obecny poziom</label>
                    <select
                      id="level"
                      name="level"
                      value={formData.level}
                      onChange={handleInputChange}
                    >
                      <option value="">Wybierz poziom</option>
                      <option value="slaby">Słaby</option>
                      <option value="sredni">Średni</option>
                      <option value="dobry">Dobry</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Dodatkowe informacje</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Opisz swoje cele lub zadaj pytanie..."
                    />
                  </div>

                  <button type="submit" className="course-submit-button">
                    Zapisz się na lekcję
                  </button>

                  <p className="form-note">
                    * Pola wymagane. Pierwsza lekcja GRATIS!
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CoursePage;
